import { Bio, BioThemeInfo, Platform, BioLink } from '../types';
import { apiClient, ApiError } from './apiClient';

export const bioApi = {
    async getThemes(): Promise<BioThemeInfo[]> {
        try {
            const response = await apiClient.request<BioThemeInfo[]>('/api/bio/themes', {
                method: 'GET',
            });

            if (!response.success || !response.data) {
                throw new ApiError(404, 'Failed to fetch themes');
            }

            return response.data;
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError(404, 'Failed to fetch themes');
        }
    },

    async getBio(bioId: string): Promise<Bio> {
        try {
            const response = await apiClient.request<Bio>(`/api/bio/${bioId}`, {
                method: 'GET',
            });

            if (!response.success || !response.data) {
                throw new ApiError(404, 'Bio not found');
            }

            return response.data;
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError(404, 'Bio not found');
        }
    },

    async createBio(bioData: {
        path: string;
        name?: string;
        title: string;
        coverImage?: string;
        description?: string;
        platforms?: Platform[];
        links?: BioLink[];
        theme?: string;
    }): Promise<Bio> {
        try {
            const response = await apiClient.request<Bio>('/api/bio', {
                method: 'POST',
                body: JSON.stringify(bioData),
            });

            if (!response.success || !response.data) {
                throw new ApiError(400, 'Failed to create bio');
            }

            return response.data;
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError(400, 'Failed to create bio');
        }
    },

    async updateBio(bioData: {
        id: string;
        path?: string;
        name?: string;
        title?: string;
        coverImage?: string;
        description?: string;
        platforms?: Platform[];
        links?: BioLink[];
        theme?: string;
    }): Promise<void> {
        try {
            const response = await apiClient.request<any>(
                `/api/bio/${bioData.id}`,
                {
                    method: 'PUT',
                    body: JSON.stringify(bioData),
                }
            );

            if (!response.success) {
                throw new ApiError(400, 'Failed to update bio');
            }
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError(400, 'Failed to update bio');
        }
    },

    async deleteBio(bioId: string): Promise<void> {
        try {
            await apiClient.request<{ message: string }>(
                `/api/bio/${bioId}`,
                {
                    method: 'DELETE',
                }
            );
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError(400, 'Failed to delete bio');
        }
    },

    async previewBio(previewData: {
        name?: string;
        title: string;
        coverImage?: string;
        description?: string;
        platforms?: Platform[];
        links?: BioLink[];
        theme?: string;
    }): Promise<string> {
        try {
            const response = await fetch(`${apiClient['baseUrl']}/api/bio/preview`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(previewData),
            });

            if (!response.ok) {
                throw new ApiError(response.status, 'Failed to generate preview');
            }

            return await response.text();
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError(500, 'Failed to generate preview');
        }
    },
};

